import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Calendar, Clock, Mail, Phone, MapPin, Plus, FileText, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

interface Booking {
  id: string;
  user_id: string;
  quote_id?: string;
  invoice_id?: string;
  name: string;
  email: string;
  phone: string;
  service_date: string;
  service_time: string;
  duration_minutes: number;
  address: string;
  package_tier: string;
  vehicle_size?: string;
  total_price: number;
  notes?: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  reminder_sent: boolean;
  created_at: string;
  updated_at: string;
}

interface Quote {
  id: string;
  customer_name: string;
  total_amount: number;
}

interface Invoice {
  id: string;
  invoice_number: string;
  total_amount: number;
}

export function BookingsManager() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('all');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('user_id', user.id)
        .order('service_date', { ascending: true });

      if (error) throw error;
      setBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (bookingId: string, newStatus: Booking['status']) => {
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', bookingId);

      if (error) throw error;

      toast.success(`Booking ${newStatus}`);
      fetchBookings();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update booking status');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'upcoming') return booking.service_date >= today;
    if (filter === 'past') return booking.service_date < today;
    return true;
  });

  const upcomingBookings = filteredBookings.filter(b => b.service_date >= today);
  const pastBookings = filteredBookings.filter(b => b.service_date < today);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-slate-600 font-bold">Loading bookings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-black uppercase">Bookings</h2>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase"
        >
          <Plus className="w-4 h-4" />
          New Booking
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-white p-1 rounded-2xl border-2 border-slate-100">
        {(['all', 'upcoming', 'past'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex-1 py-2 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all ${
              filter === f
                ? 'bg-slate-900 text-white shadow-lg'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-white rounded-[2rem] border-2 border-slate-100">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="font-bold text-sm">No bookings yet</p>
          <p className="text-xs mt-1">Create bookings manually or they'll appear here when customers book online</p>
        </div>
      ) : (
        <>
          {/* Upcoming Bookings */}
          {upcomingBookings.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-black uppercase text-slate-400 text-xs tracking-widest">Upcoming</h3>
              <div className="grid gap-4">
                {upcomingBookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past Bookings */}
          {pastBookings.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-black uppercase text-slate-400 text-xs tracking-widest">Past</h3>
              <div className="grid gap-4">
                {pastBookings.map((booking) => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Booking Modal */}
      {showCreateModal && (
        <CreateBookingModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchBookings();
          }}
        />
      )}
    </div>
  );
}

// Booking Card Component
function BookingCard({
  booking,
  onStatusChange,
}: {
  booking: Booking;
  onStatusChange: (id: string, status: Booking['status']) => void;
}) {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'completed':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  useEffect(() => {
    const fetchLinked = async () => {
      if (booking.quote_id) {
        const { data } = await supabase
          .from('quotes')
          .select('id, customer_name, total_amount')
          .eq('id', booking.quote_id)
          .single();
        if (data) setQuote(data);
      }

      if (booking.invoice_id) {
        const { data } = await supabase
          .from('invoices')
          .select('id, invoice_number, total_amount')
          .eq('id', booking.invoice_id)
          .single();
        if (data) setInvoice(data);
      }
    };

    if (booking.quote_id || booking.invoice_id) {
      fetchLinked();
    }
  }, [booking.quote_id, booking.invoice_id]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white border-2 border-slate-100 rounded-[2rem] p-6 shadow-sm hover:border-purple-200 transition-colors">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-[8px] font-black px-2 py-0.5 rounded uppercase border ${getStatusColor(booking.status)}`}>
              {booking.status}
            </span>
            {booking.reminder_sent && (
              <span className="text-[7px] font-bold text-slate-400 uppercase flex items-center gap-1">
                <CheckCircle2 className="w-2 h-2" />
                Reminder Sent
              </span>
            )}
          </div>
          <h3 className="text-lg font-black uppercase text-slate-900">{booking.name}</h3>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600 font-bold">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3 text-purple-600" />
              {formatDate(booking.service_date)}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-purple-600" />
              {booking.service_time} ({booking.duration_minutes} min)
            </div>
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 text-purple-600" />
              {booking.address || 'Address not provided'}
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500 font-bold">
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {booking.email}
            </div>
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {booking.phone}
            </div>
          </div>
          {booking.package_tier && (
            <p className="text-xs font-black text-purple-600 mt-2 uppercase">
              Package: {booking.package_tier}
            </p>
          )}
          {booking.vehicle_size && (
            <p className="text-xs font-bold text-slate-500">
              Vehicle: {booking.vehicle_size}
            </p>
          )}
          {booking.notes && (
            <p className="text-xs text-slate-500 mt-2 italic">"{booking.notes}"</p>
          )}

          {/* Linked Quote/Invoice */}
          <div className="flex gap-2 mt-3">
            {quote && (
              <a
                href={`/quote/${quote.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[9px] font-black text-purple-600 hover:text-purple-700 uppercase flex items-center gap-1 bg-purple-50 px-2 py-1 rounded"
              >
                <FileText className="w-3 h-3" />
                Quote: ${quote.total_amount.toFixed(2)}
              </a>
            )}
            {invoice && (
              <span className="text-[9px] font-black text-green-600 uppercase flex items-center gap-1 bg-green-50 px-2 py-1 rounded">
                <CheckCircle2 className="w-3 h-3" />
                Invoice: {invoice.invoice_number}
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <p className="text-2xl font-black italic text-slate-900">${booking.total_price.toFixed(2)}</p>
          <p className="text-[8px] font-bold text-slate-400 uppercase mt-1">Total Price</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        {booking.status === 'pending' && (
          <Button
            size="sm"
            onClick={() => onStatusChange(booking.id, 'confirmed')}
            className="bg-green-600 hover:bg-green-700 rounded-xl font-black uppercase text-[9px]"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Confirm
          </Button>
        )}
        {booking.status === 'confirmed' && (
          <Button
            size="sm"
            onClick={() => onStatusChange(booking.id, 'completed')}
            className="bg-blue-600 hover:bg-blue-700 rounded-xl font-black uppercase text-[9px]"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Complete
          </Button>
        )}
        {(booking.status === 'pending' || booking.status === 'confirmed') && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onStatusChange(booking.id, 'cancelled')}
            className="rounded-xl font-black uppercase text-[9px] text-red-600 border-red-200 hover:bg-red-50"
          >
            <XCircle className="w-3 h-3 mr-1" />
            Cancel
          </Button>
        )}
        {booking.status === 'cancelled' && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onStatusChange(booking.id, 'pending')}
            className="rounded-xl font-black uppercase text-[9px]"
          >
            <AlertCircle className="w-3 h-3 mr-1" />
            Reopen
          </Button>
        )}
      </div>
    </div>
  );
}

// Create Booking Modal
function CreateBookingModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_date: new Date().toISOString().split('T')[0],
    service_time: '09:00',
    duration_minutes: 60,
    address: '',
    package_tier: 'Standard',
    vehicle_size: '',
    total_price: 0,
    notes: '',
  });
  const [creating, setCreating] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      const { error } = await supabase
        .from('bookings')
        .insert([{
          user_id: user.id,
          ...formData,
          status: 'pending',
          reminder_sent: false,
        }]);

      if (error) throw error;

      toast.success('Booking created!');
      onSuccess();
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking');
    } finally {
      setCreating(false);
    }
  };

  const inputClass = "w-full h-12 rounded-xl border border-slate-200 font-bold focus:ring-2 ring-purple-500 outline-none";
  const labelClass = "text-[10px] font-black text-slate-500 uppercase mb-1 block";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-black text-slate-900 uppercase mb-4">Create New Booking</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Customer Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={inputClass}
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className={labelClass}>Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={inputClass}
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Phone *</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className={inputClass}
              placeholder="0412 345 678"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Service Date *</label>
              <Input
                type="date"
                value={formData.service_date}
                onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Service Time *</label>
              <Input
                type="time"
                value={formData.service_time}
                onChange={(e) => setFormData({ ...formData, service_time: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Duration (minutes)</label>
              <Input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Total Price ($)</label>
              <Input
                type="number"
                value={formData.total_price}
                onChange={(e) => setFormData({ ...formData, total_price: parseFloat(e.target.value) || 0 })}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Service Address</label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className={inputClass}
              placeholder="123 Main St, Suburb"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Package Tier</label>
              <select
                value={formData.package_tier}
                onChange={(e) => setFormData({ ...formData, package_tier: e.target.value })}
                className={inputClass}
              >
                <option value="Basic">Basic</option>
                <option value="Standard">Standard</option>
                <option value="Premium">Premium</option>
                <option value="Deluxe">Deluxe</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Vehicle Size (if applicable)</label>
              <Input
                value={formData.vehicle_size}
                onChange={(e) => setFormData({ ...formData, vehicle_size: e.target.value })}
                className={inputClass}
                placeholder="Small, Medium, Large, SUV"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full rounded-xl border border-slate-200 p-3 font-bold text-sm outline-none focus:ring-2 ring-purple-500 resize-none"
              rows={3}
              placeholder="Any special instructions..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSubmit}
              disabled={creating}
              className="flex-1 bg-purple-600 hover:bg-purple-700 rounded-2xl font-black uppercase"
            >
              {creating ? 'Creating...' : 'Create Booking'}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-2xl"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
